import i18next from 'i18next';

import {type SelectOption} from '@famly/mf_input_select';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import {AUTOMATIC_BILLING_OPTIONS, BILLING_FREQUENCY_OPTIONS} from '../constants';
import {
    transformChildrenToChildrenOptions,
    transformDiscountToDiscountOption,
    transformTuitionOrFeeToTuitionAndFeeOption,
} from '../transformers/select-options-transformers';
import type * as Types from '../types';
import type {SimplifiedUsBillingPlan} from './use-simplified-us-billing-plan-data';

export type SelectOptions = {
    childrenOptions: Array<SelectOption>;
    tuitionOptions: Array<Types.TuitionAndFeeOption>;
    feeOptions: Array<Types.TuitionAndFeeOption>;
    discountOptions: Array<Types.DiscountOption>;
    billingFrequencyOptions: Array<Types.BillingFrequencyOption>;
    pricingGroupOptions: Array<Types.PricingGroupOption>;
};
type Args = {
    state: Types.State;
    permissions: {
        canAddNewItem: boolean;
        canAddNewDiscount: boolean;
        isAutomaticDecouplingEnabled: boolean;
    };
    pricingGroups: SimplifiedUsBillingPlan['pricingGroups'];
};

const useSharedSelectOptions = ({
    state: {
        childrenState: {allChildren},
        tuitionsState: {allTuitions},
        feesState: {allFees},
        pricingGroupState: {selectedPricingGroup, monthlyPeriodPricingGroups, weeklyPeriodPricingGroups},
        discountState: {allDiscounts},
        selectedBillingType,
    },
    permissions: {canAddNewItem, canAddNewDiscount, isAutomaticDecouplingEnabled},
    pricingGroups,
}: Args): SelectOptions => {
    const childrenOptions = transformChildrenToChildrenOptions(allChildren);

    const addNewItemOption: Types.TuitionAndFeeOption = {
        label: `+ ${i18next.t('react.simplifiedUsBillingPlan.translations.json.addNewItem.addNewItemOption.label')}`,
        value: 'newItem',
        type: 'add-new-item',
    };
    const tuitionOptions = (): Array<Types.TuitionAndFeeOption> => {
        const options = allTuitions
            .map(tuition =>
                transformTuitionOrFeeToTuitionAndFeeOption({
                    tuitionOrFee: tuition,
                    selectedPricingGroup,
                    monthlyPeriodPricingGroups,
                    weeklyPeriodPricingGroups,
                }),
            )
            .filter(hasValue);
        return canAddNewItem ? [...options, addNewItemOption] : options;
    };

    const feeOptions = (): Array<Types.TuitionAndFeeOption> => {
        const options = allFees
            .map(fee =>
                transformTuitionOrFeeToTuitionAndFeeOption({
                    tuitionOrFee: fee,
                    selectedPricingGroup,
                    monthlyPeriodPricingGroups,
                    weeklyPeriodPricingGroups,
                }),
            )
            .filter(hasValue);
        const feeOptionsToDisplay = options.filter(
            option => option.type === selectedBillingType || option.type === 'add-new-item',
        );
        return canAddNewItem && selectedBillingType ? [...feeOptionsToDisplay, addNewItemOption] : feeOptionsToDisplay;
    };

    const discountOptions = () => {
        const addNewDiscountOption: Types.DiscountOption = {
            label: `+ ${i18next.t(
                'react.simplifiedUsBillingPlan.translations.json.addNewItem.addNewItemOption.label',
            )}`,
            value: 'newItem',
            type: 'add-new-item',
        };
        const options = allDiscounts.map(discount => transformDiscountToDiscountOption(discount));
        return canAddNewDiscount && selectedBillingType ? [...options, addNewDiscountOption] : options;
    };

    const pricingGroupOptions: Array<Types.PricingGroupOption> = pricingGroups.map(pricingGroup => ({
        label: pricingGroup.title,
        value: pricingGroup.id,
    }));

    return {
        childrenOptions,
        tuitionOptions: tuitionOptions(),
        feeOptions: feeOptions(),
        discountOptions: discountOptions(),
        billingFrequencyOptions: isAutomaticDecouplingEnabled ? BILLING_FREQUENCY_OPTIONS : AUTOMATIC_BILLING_OPTIONS,
        pricingGroupOptions,
    };
};

export {useSharedSelectOptions};
